import { HTMLAttributes, useState, useEffect } from "react";
import { SpaceProps, color, LayoutProps } from "styled-system";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconPrefix, IconName } from "@fortawesome/pro-light-svg-icons";
import styled from "styled-components";

import "./fontawesome";
import useThemeColor from "../useThemeColor.hook";
import Box from "../Box/Box";
import { iconMapper } from "./icons/IconMapper";

export type IconWeight = "regular" | "solid" | "light" | "thin" | "duotone";
export const availableIcons = [
  "comment-alt",
  "map-signs",
  "comments-alt",
  "check",
  "user-alt",
  "star",
  "check-circle",
  "search",
  "angle-up",
  "angle-down",
  "angle-left",
  "angle-right",
  "exclamation-circle",
  "exclamation-triangle",
  "cars",
  "caravan-alt",
  "car-wash",
  "car-crash",
  "car-alt",
  "calendar-alt",
  "cog",
  "book",
  "bars",
  "arrow-left",
  "arrow-right",
  "arrow-up",
  "arrow-down",
  "info-circle",
  "times",
  "plus",
  "pound-sign",
  "gavel",
  "charging-station",
  "gas-pump",
  "bell-slash",
  "bell",
  "truck-moving",
  "truck-pickup",
  "truck-container",
  "car-side",
  "car-mechanic",
  "car-garage",
  "car",
  "tire",
  "road",
  "phone-alt",
  "desktop",
  "mobile",
  "tv",
  "award",
  "bullseye",
  "clipboard-list-check",
  "clipboard-list",
  "calendar-check",
  "bookmark",
  "ballot-check",
  "ballot",
  "address-book",
  "ban",
  "sign-out",
  "sticky-note",
  "truck",
  "motorcycle",
  "twitter",
  "linkedin-in",
  "map-marker",
  "play",
  "trash",
  "filter",
  "chevron-up",
  "chevron-right",
  "chevron-down",
  "chevron-left",
  "chevron-double-left",
  "chevron-double-right",
  "file-certificate",
  "certificate",
  "fuel",
  "gas-pump",
  "charging-station",
  "fuel-electric",
  "history",
  "chart-line",
  "pricing",
  "users",
  "owners",
  "mechanical-report",
  "wrench",
  "tachometer-fast",
  "mileage",
  "envelope",
];

const availableIconsInternal = [
  "no-certificate",
  "no-history",
  "no-mechanical-report",
  "transmission",
];

export const brandIcons = ["twitter", "linkedin-in"];

export interface IconProps extends SpaceProps, HTMLAttributes<HTMLDivElement> {
  /**
  The icon name. Accepts any fontawesome icon name: see https://fontawesome.com/icons?d=gallery or one of the following internal icons:  
  no-certificate, no-history, no-mechanical-report, transmission.  
  Aliases: certificate = file-certificate, fuel = gas-pump, fuel-electric = charging-station, mechanical-report = wrench, mileage = tachometer-fast, owners = users, pricing = chart-line  */
  name: string;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
  The icon weight. It can be one of the following values: "regular" | "solid" | "light"
  Currently not supported for internal icons.
  */
  weight?: IconWeight;
  /**
  The icon color. Accepts hex color code or pattern library collor.
  */
  color?: string;
  /**
   * Width of element. Can be in pixels or number or standard css keywords("auto", "100%").
   * Also accepts responsive values as an array. Each value coresponds to theme breakpoints.
   * Example: ["100%", 300, "50%", "auto"]
   */
  width?: LayoutProps["width"];
  /**
   * Height of element. Can be in pixels or number or standard css keywords("auto", "100%").
   * Also accepts responsive values as an array. Each value coresponds to theme breakpoints.
   * Example: ["100%", 300, "50%", "auto"]
   */
  height?: LayoutProps["height"];
  className?: string;
  css?: string;
}

const iconPrefix = {
  light: "fal",
  regular: "far",
  solid: "fas",
};

const aliases = {
  "sign-post": "map-signs",
  comment: "comment-alt",
  certificate: "file-certificate",
  fuel: "gas-pump",
  "fuel-electric": "charging-station",
  pricing: "chart-line",
  owners: "users",
  mileage: "tachometer-fast",
  "mechanical-report": "wrench",
};

const IconContainer = styled(Box)`
  svg:first-of-type {
    width: 100%;
    height: 100%;
    display: block;
  }
  ${(props) => props.css}
`;

const SvgIconContainer = styled(IconContainer)`
  svg:first-of-type {
    fill: currentColor;
    display: block;
  }
  ${color}
  ${(props) => props.css}
`;

const SVGIconWithFetch = ({
  weight,
  name,
  color,
  width,
  height,
  className,
  ...props
}: IconProps) => {
  const [svg, setSvg] = useState("");
  const iconRootUrl = process.env.STORYBOOK_ICON_URL || process.env.ICON_URL;
  const iconUrl = `${iconRootUrl}svgs/${weight}/${name}.svg`;

  useEffect(() => {
    fetch(iconUrl)
      .then((response) => {
        if (!response.ok) {
          throw response;
        }
        return response.text();
      })
      .then((text) => setSvg(text))
      .catch((e) => {
        setSvg("");
      });
  }, [weight, name, iconUrl]);

  return svg ? (
    <SvgIconContainer
      data-testid={`svg-icon-container-${name}`}
      dangerouslySetInnerHTML={{ __html: svg }}
      width={width}
      height={height}
      color={color}
      className={className}
      {...props}
    />
  ) : null;
};

export default function Icon({
  name,
  weight = "regular",
  color = "cta-tertiary-text-color-default",
  width,
  height,
  className,
  ...props
}: IconProps) {
  const isIconAvailable =
    [...availableIcons, ...availableIconsInternal].includes(name) &&
    Boolean(iconPrefix[weight]);
  const hexColor = useThemeColor(color);
  const iconName = aliases[name] ?? name;
  const isInternalIcon = availableIconsInternal.includes(iconName);

  const prefix =
    !isInternalIcon && brandIcons.includes(iconName)
      ? "fab"
      : iconPrefix[weight];

  if (!name) {
    return null;
  }

  return isIconAvailable ? (
    <IconContainer
      width={width}
      height={height}
      data-testid={`icon-container-${iconName}`}
      {...props}
    >
      {!isInternalIcon ? (
        <FontAwesomeIcon
          icon={[prefix as IconPrefix, iconName as IconName]}
          color={hexColor}
          className={className}
        />
      ) : (
        iconMapper(iconName, hexColor, className)
      )}
    </IconContainer>
  ) : (
    <SVGIconWithFetch
      name={iconName}
      weight={weight}
      color={color}
      width={width}
      height={height}
      className={className}
      {...props}
    />
  );
}
